import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  DollarSign, TrendingUp, AlertTriangle, Wallet, FileText, FolderOpen,
  CalendarClock, Activity, ArrowRight, HandCoins,
} from "lucide-react";
import Layout from "../components/Layout";
import { formatCurrency, formatDate, formatDateTime } from "../lib/propiedadUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORIDAD = {
  alta: { label: "Alta", className: "bg-red-100 text-red-700" },
  media: { label: "Media", className: "bg-amber-100 text-amber-700" },
  baja: { label: "Baja", className: "bg-blue-100 text-blue-700" },
  info: { label: "Info", className: "bg-indigo-100 text-indigo-700" },
};

const ACCION_LABEL = {
  registrar_pago: "Registrar pago",
  renovar: "Renovar",
  ver_renovacion: "Ver renovación",
  subir_documento: "Subir documento",
};

export default function Inicio({ user, onLogout }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/dashboard/operativo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        // se muestra estado vacío
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-20 text-gray-500">Cargando panel...</div>
      </Layout>
    );
  }

  const ind = data?.indicadores || {};
  const pendientes = data?.pendientes || [];
  const venc = data?.proximos_vencimientos || { cobros: [], contratos: [] };
  const actividad = data?.actividad_reciente || [];

  const indicadores = [
    { label: "Rentas esperadas del mes", value: formatCurrency(ind.rentas_esperadas_mes), icon: DollarSign, color: "text-gray-700" },
    { label: "Rentas cobradas", value: formatCurrency(ind.rentas_cobradas_mes), icon: TrendingUp, color: "text-emerald-600" },
    { label: "Cartera vencida", value: formatCurrency(ind.cartera_vencida), icon: AlertTriangle, color: "text-red-600" },
    { label: "Por entregar a propietarios", value: formatCurrency(ind.por_entregar_propietarios), icon: HandCoins, color: "text-indigo-600", hint: "Cobrado − gastos y comisiones del mes" },
    { label: "Gastos de propiedades (mes)", value: formatCurrency(ind.gastos_propiedades_mes), icon: Wallet, color: "text-gray-700" },
    { label: "Contratos por vencer (90 días)", value: ind.contratos_por_vencer ?? 0, icon: FileText, color: "text-amber-600" },
    { label: "Expedientes incompletos", value: ind.expedientes_incompletos ?? 0, icon: FolderOpen, color: "text-amber-600" },
    { label: "Saldo pendiente total", value: formatCurrency(ind.saldo_pendiente), icon: DollarSign, color: "text-gray-700" },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="inicio-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Inicio</h1>
            <p className="text-sm text-gray-500 mt-1">Lo que requiere tu atención hoy</p>
          </div>
          <Button onClick={() => navigate("/propiedades")} variant="outline" data-testid="ver-propiedades-btn">
            Ver propiedades <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Indicadores */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="indicadores-grid">
          {indicadores.map((it, i) => (
            <Card key={i}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 truncate">{it.label}</p>
                    <p className={`text-xl font-bold mt-1 ${it.color}`}>{it.value}</p>
                    {it.hint && <p className="text-[10px] text-gray-400 mt-0.5">{it.hint}</p>}
                  </div>
                  <it.icon className={`w-5 h-5 shrink-0 ${it.color} opacity-60`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pendientes de hoy */}
        <Card data-testid="pendientes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Pendientes de hoy ({pendientes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {pendientes.length === 0 ? (
              <p className="px-6 py-8 text-center text-sm text-gray-500">Sin pendientes. Todo al corriente 🎉</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2">Prioridad</th>
                    <th className="px-4 py-2">Pendiente</th>
                    <th className="px-4 py-2">Propiedad</th>
                    <th className="px-4 py-2 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.map((p, i) => {
                    const pr = PRIORIDAD[p.prioridad] || PRIORIDAD.info;
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${pr.className}`}>{pr.label}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-900">{p.pendiente}</td>
                        <td className="px-4 py-2.5">
                          <button
                            onClick={() => navigate(`/propiedades/${p.property_id}`)}
                            className="text-indigo-600 hover:underline font-medium"
                            data-testid={`pendiente-propiedad-${i}`}
                          >
                            {p.propiedad}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/propiedades/${p.property_id}`)} data-testid={`pendiente-accion-${i}`}>
                            {ACCION_LABEL[p.accion] || "Ver"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Próximos vencimientos */}
          <Card data-testid="vencimientos-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-gray-500" /> Próximos vencimientos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {venc.cobros.length === 0 && venc.contratos.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">Sin vencimientos próximos</p>
              ) : (
                <>
                  {venc.cobros.map((v, i) => (
                    <button
                      key={`c-${i}`}
                      onClick={() => v.property_id && navigate(`/propiedades/${v.property_id}`)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm text-gray-900 truncate">{v.descripcion}</span>
                        <span className="block text-xs text-gray-500">Cobro · {formatDate(v.fecha)}</span>
                      </span>
                      <span className="text-sm font-semibold text-gray-900 shrink-0">{formatCurrency(v.monto)}</span>
                    </button>
                  ))}
                  {venc.contratos.map((v, i) => (
                    <button
                      key={`k-${i}`}
                      onClick={() => v.property_id && navigate(`/propiedades/${v.property_id}`)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm text-gray-900 truncate">{v.descripcion}</span>
                        <span className="block text-xs text-amber-600">Contrato · {formatDate(v.fecha)}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
            </CardContent>
          </Card>

          {/* Actividad reciente */}
          <Card data-testid="actividad-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-500" /> Actividad reciente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {actividad.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">Sin actividad reciente</p>
              ) : (
                actividad.map((a, i) => (
                  <div key={i} className="flex gap-3">
                    <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                      a.tipo === "pago" ? "bg-emerald-500" : a.tipo === "gasto" ? "bg-red-400" : "bg-gray-400"
                    }`} />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900">{a.descripcion}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(a.fecha)}
                        {a.propiedad && (
                          <>
                            {" · "}
                            <button onClick={() => navigate(`/propiedades/${a.property_id}`)} className="text-indigo-600 hover:underline">
                              {a.propiedad}
                            </button>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
